import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { JobCard } from "@/components/JobCard";
import { toast } from "sonner";
import { Loader2, Upload, FileText, CheckCircle2, Clock, Sparkles, Bell, Bookmark } from "lucide-react";

export default function ApplicantDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [states, setStates] = useState([]);
  const [profile, setProfile] = useState({ full_name: "", phone: "", city: "", state: "", certifications: "", cover_letter_text: "", consent_store_resume: true, alert_optin: false, digest_frequency: "daily" });
  const [membership, setMembership] = useState(null);
  const [apps, setApps] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState({});
  const resumeRef = useRef(); const coverRef = useRef();

  useEffect(() => {
    api.get("/states").then(({ data }) => setStates(data));
    Promise.all([
      api.get("/applicant/profile"),
      api.get("/applicant/applications"),
      api.get("/saved-jobs"),
    ]).then(([p, a, s]) => {
      if (p.data.profile && Object.keys(p.data.profile).length) setProfile((f) => ({ ...f, ...p.data.profile }));
      setFiles({ resume: p.data.profile?.resume_file_id, cover: p.data.profile?.cover_letter_file_id });
      setMembership({ active: p.data.membership_active, until: p.data.membership_active_until });
      setApps(a.data);
      setSaved(s.data);
    }).finally(() => setLoading(false));
  }, []);

  const onToggleSave = (jobId, isSaved) => { if (!isSaved) setSaved((prev) => prev.filter((j) => j.job_id !== jobId)); };

  const save = async () => {
    setSaving(true);
    try { await api.post("/applicant/profile", profile); toast.success("Profile saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/applicant/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => ({ ...f, [kind === "resume" ? "resume" : "cover"]: data.file_id }));
      toast.success(`${kind === "resume" ? "Resume" : "Cover letter"} uploaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto py-8 flex-1 w-full">
        <h1 className="font-display text-3xl font-extrabold text-navy-800">Your applicant profile</h1>

        {/* Membership status */}
        <div className={`mt-6 rounded-2xl p-6 shadow-sm border ${membership?.active ? "bg-emerald-50 border-emerald-200" : "bg-navy-800 border-navy-800 text-white"}`} data-testid="membership-status">
          {membership?.active ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><div><div className="font-display font-bold text-navy-800">Membership active</div><div className="text-sm text-emerald-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Until {new Date(membership.until).toLocaleDateString()}</div></div></div>
              <Button onClick={() => navigate("/jobs")} className="bg-navy-800 hover:bg-navy-900 text-white">Browse & apply</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3"><Sparkles className="h-6 w-6 text-amber-500" /><div><div className="font-display font-bold">No active membership</div><div className="text-sm text-slate-300">Get 30 days of unlimited applications for $10.</div></div></div>
              <Button data-testid="get-membership-btn" onClick={() => navigate("/membership")} className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold">Get membership — $10</Button>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-5">
            <h2 className="font-display text-lg font-bold text-navy-800">Profile details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Full name"><Input data-testid="profile-name" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} /></F>
              <F label="Phone"><Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></F>
              <F label="City"><Input data-testid="profile-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></F>
              <F label="State">
                <Select value={profile.state} onValueChange={(v) => setProfile({ ...profile, state: v })}>
                  <SelectTrigger data-testid="profile-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <F label="Certifications (OSHA, CSP, etc.)"><Input data-testid="profile-certs" value={profile.certifications} onChange={(e) => setProfile({ ...profile, certifications: e.target.value })} /></F>
            <F label="Default cover letter"><Textarea data-testid="profile-cover-text" value={profile.cover_letter_text} onChange={(e) => setProfile({ ...profile, cover_letter_text: e.target.value })} rows={4} /></F>
            <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 cursor-pointer">
              <span className="flex items-center gap-2 text-sm font-medium text-navy-800"><Bell className="h-4 w-4 text-amber-600" /> Email me new safety jobs in {profile.state || "my state"}</span>
              <Switch data-testid="profile-alert-optin" checked={!!profile.alert_optin} onCheckedChange={(v) => setProfile({ ...profile, alert_optin: v })} />
            </label>
            {profile.alert_optin && (
              <F label="Alert digest frequency">
                <Select value={profile.digest_frequency || "daily"} onValueChange={(v) => setProfile({ ...profile, digest_frequency: v })}>
                  <SelectTrigger data-testid="profile-digest-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily digest</SelectItem>
                    <SelectItem value="weekly">Weekly digest (Mondays)</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            )}
            <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="bg-navy-800 hover:bg-navy-900 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}</Button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
            <h2 className="font-display text-lg font-bold text-navy-800">Documents</h2>
            <p className="text-xs text-slate-400">PDF, DOC or DOCX. Max 5MB.</p>
            <UploadRow label="Resume" done={!!files.resume} inputRef={resumeRef} onPick={(f) => upload("resume", f)} testid="upload-resume" />
            <UploadRow label="Cover letter" done={!!files.cover} inputRef={coverRef} onPick={(f) => upload("cover_letter", f)} testid="upload-cover" />
            <p className="text-[11px] text-slate-400 leading-relaxed pt-2 border-t border-slate-100">By uploading, you consent to Safety Jobs storing and forwarding these files to employers when you apply.</p>
          </div>
        </div>

        {/* Saved jobs */}
        {saved.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-lg font-bold text-navy-800 mb-4 flex items-center gap-2"><Bookmark className="h-5 w-5 text-amber-500" /> Saved jobs ({saved.length})</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="saved-jobs-grid">
              {saved.map((j) => <JobCard key={j.job_id} job={j} showSave saved onToggleSave={onToggleSave} />)}
            </div>
          </div>
        )}

        {/* Applications */}
        <div className="mt-8">
          <h2 className="font-display text-lg font-bold text-navy-800 mb-4">Your applications ({apps.length})</h2>
          {apps.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-300 text-slate-500">You haven't applied to any jobs yet.</div> : (
            <div className="space-y-3">
              {apps.map((a) => (
                <div key={a.application_id} data-testid={`my-app-${a.application_id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between shadow-sm">
                  <div><div className="font-semibold text-navy-800">{a.job_title}</div><div className="text-sm text-slate-500">{a.company_name}</div></div>
                  <div className="text-right"><Badge variant="secondary" className="bg-emerald-50 text-emerald-700">Applied</Badge><div className="text-xs text-slate-400 mt-1">{new Date(a.created_at).toLocaleDateString()}</div></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

const F = ({ label, children }) => <div><Label className="text-navy-800">{label}</Label><div className="mt-1.5">{children}</div></div>;

const UploadRow = ({ label, done, inputRef, onPick, testid }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
    <div className="flex items-center gap-2 text-sm"><FileText className={`h-4 w-4 ${done ? "text-emerald-600" : "text-slate-400"}`} /><span className="font-medium text-navy-800">{label}</span>{done && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}</div>
    <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => onPick(e.target.files[0])} data-testid={`${testid}-input`} />
    <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid={testid} className="gap-1.5"><Upload className="h-3.5 w-3.5" /> {done ? "Replace" : "Upload"}</Button>
  </div>
);
